'use client';

import clsx from 'clsx';
import { useContext, useEffect, useRef, useState } from 'react';
import configBase from 'config-base';
import Image from 'next/image';
import { ShoppingCartStepType } from '@/types/shoppingCart';
import useHover from '@/hooks/useHover';
import useMediaQuery from '@/hooks/useMediaQuery';
import Link from '@/components/Link';
import List from './List';
import HeaderContext, { HeaderContextType } from '@/context/HeaderContext';
import Backdrop from '@/components/Backdrop';
import CartPopupApiService from '@/api/cartPopup';
import useDeleteProduct from '@/hooks/product/useDelete';
import useCartItems from '@/hooks/useCartItems';
import { apiClient } from '@/services/base';
import { cookieStorage } from '@/services/cookie';
import { CartPopupResponse } from '@/api/cartPopup/types';
import currencyFormatting from '@/utils/currencyFormatting';
import Button from '@/components/Button/BaseButton';
import ListrakAnalytic from '@/lib/Listrak/ListrakAnalytic';

import styles from './CartPopup.module.css';

const cartPopupApiService = new CartPopupApiService(apiClient);
const listrakAnalytic = new ListrakAnalytic(cookieStorage);

const CartPopup = ({ miniCartPopupData, isAdsSession }: { miniCartPopupData: CartPopupResponse | undefined, isAdsSession: boolean }) => {
  const { height: topPosition, isCartOpened, setIsCardOpened } = useContext(HeaderContext) as HeaderContextType;
  const [miniCartData, setMiniCartData] = useState<CartPopupResponse | undefined>(miniCartPopupData);
  const [height, setHeight] = useState<number>(445);
  const { deleteProduct } = useDeleteProduct();
  const [showTopShade, setShowTopShade] = useState<boolean>(false);
  const [showBottomShade, setShowBottomShade] = useState<boolean>(false);
  const hoverRef = useRef<HTMLDivElement>(null);
  const { isHover, handleHoverLeave } = useHover(hoverRef);
  const mobile: boolean = useMediaQuery('(max-width:1279px)');
  const { cartItems } = useCartItems(miniCartData);

  const getMiniCartPopupData = async () => {
    const res = await cartPopupApiService.getList();

    if (!res) {
      setMiniCartData({ products: [], orderId: 0, subTotal: 0 });
      return { products: [], orderId: 0, subTotal: 0 };
    }
    setMiniCartData(res?.data);
    return res?.data;
  };

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = ({ target }: MouseEvent): void => {
    if (!hoverRef.current?.contains(target as Node)) {
      setIsCardOpened(false);
    }
  };

  useEffect(() => {
    if (!isHover) return;
    const element = document.getElementById('top-banner-edit');
    const headerHeight = element ? topPosition + 102 : topPosition + 82;
    setHeight(window.innerHeight - headerHeight >= 445 ? 445 : window.innerHeight - headerHeight);
    getMiniCartPopupData();
  }, [isHover]);

  useEffect(() => {
    hoverCartScroll();
  }, [miniCartData]);

  useEffect(() => {
    if (document) {
      setTimeout(() => {
        const htmlRoot = document?.querySelector('html');
        if (isCartOpened) {
          if (mobile && window.scrollY < topPosition) {
            window.scrollTo({ top: 0 });
          }
          htmlRoot?.classList.add('preventScroll');
        } else {
          htmlRoot?.classList.remove('preventScroll');
        }
      }, 0);
    }
  }, [isCartOpened]);

  const hoverCartScroll = () => {
    const itemList: HTMLElement | null = document.getElementById('product-list');
    if (!itemList) return;
    const scrollH = itemList?.scrollHeight;

    if (scrollH <= itemList.clientHeight) {
      setShowTopShade(false);
      setShowBottomShade(false);
      return;
    }

    const scrollT = itemList.scrollTop;
    const scrollB = Math.abs(scrollH - scrollT - itemList.clientHeight);

    if (scrollT > 0) {
      setShowTopShade(true);
    } else {
      setShowTopShade(false);
    }

    if (scrollB > 1) {
      setShowBottomShade(true);
    } else {
      setShowBottomShade(false);
    }
  };

  const handleClick = () => {
    if (mobile) {
      setIsCardOpened(!isCartOpened);
    } else {
      window.location.href = configBase.mainSiteUrl + ShoppingCartStepType.ShoppingCart;
    }
  };

  const handleClose = () => {
    setIsCardOpened(false);
    handleHoverLeave();
  };

  return (
    <div ref={hoverRef} className="relative">
      <div
        id="cart-icon"
        className={clsx(styles.cartItemsContainer, { [styles.cartActive]: isCartOpened })}
        onClick={handleClick}
      >
        <span className={clsx(styles.badget, { hidden: !(cartItems && cartItems > 0) })} id="badget">
          {cartItems || 0}
        </span>
      </div>

      <div
        id="cart-popup"
        className={clsx(styles.cartPopup, {
          [styles.cartPopupBodySlideLeftAnimation]: isCartOpened && miniCartData,
        })}
        style={mobile ? { top: `${topPosition}px`, height: `calc(100% - ${topPosition}px)` } : {}}
      >
        {miniCartData && isHover && (
          <div className={styles.cartContent}>
            {miniCartData.products.length > 0 ? (
              <div className={styles.withItems}>
                <div className={styles.header}>
                  <div>
                    {Boolean(cartItems) ? cartItems : 0} {cartItems == 1 ? 'Item' : 'Items'}
                  </div>
                  <Link href={configBase.shoppingCart} showCursorPointer>
                    View Cart
                  </Link>
                  <div
                    id="top-shade"
                    className={styles.topShade}
                    style={{ display: `${showTopShade ? 'block' : 'none'}` }}
                  ></div>
                </div>
                <div
                  id="product-list"
                  className={styles.list}
                  style={!mobile ? { maxHeight: `${height}px` } : {}}
                  onScroll={hoverCartScroll}
                >
                  <List
                    items={miniCartData}
                    orderId={miniCartData.orderId}
                    deleteItem={async (data) => {
                      await deleteProduct(data);
                      const miniCartPopupData = await getMiniCartPopupData();
                      if (miniCartPopupData) {
                        await listrakAnalytic.applyProducts({
                          products: miniCartPopupData.products,
                          total: miniCartPopupData.subTotal.toString(),
                        });
                      }
                    }}
                  />
                </div>

                <div className={styles.footer}>
                  <div className={styles.footerContainer}>
                    <div
                      id="bottom-shade"
                      className={styles.bottomShade}
                      style={{ display: `${showBottomShade ? 'block' : 'none'}` }}
                    ></div>
                    <div className={styles.subTotal}>
                      <span>Subtotal:</span>
                      <span>{currencyFormatting(miniCartData.subTotal)}</span>
                    </div>
                    <Button
                      id="cart-proceed-btn"
                      classes={`${styles.proceedCheckoutButton} btnSecondary`}
                      label={<p className={styles.proceedCheckoutButtonText}>Proceed To Checkout</p>}
                      onClick={() => (window.location.href = configBase.mainSiteUrl + ShoppingCartStepType.ShoppingCart)}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.empty}>
                <p className={styles.emptyText}>Your shopping cart is empty</p>
                <div className={styles.cartImage}>
                  <Image fill src="/marketplace-static/images/icons/shoping-cart-empty.svg" alt="shoppin-cart-empty" />
                </div>

                <div className={styles.buttonContainer}>
                  <Button
                    classes={`${styles.proceedCheckoutButton} btnSecondary`}
                    label={<p className={styles.proceedCheckoutButtonText}>Keep Shopping</p>}
                    onClick={handleClose}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {mobile && isCartOpened && miniCartData && (
        <Backdrop style={{ top: `${topPosition}px` }} className={styles.bodyBackdrop} onClick={handleClose} />
      )}
    </div>
  );
};

export default CartPopup;
